import type { MouseEventHandler } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import type { LinkProps } from "@mui/material"
import { Box, ButtonBase, LinearProgress, Link, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import Autoplay from "embla-carousel-autoplay"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"

import { gsap, useGSAP } from "@ggs/ui/local/gsap"
import { truncateString } from "@ggs/utils"
import type { FC, FCWC, ResponsiveImageType } from ".."
import { Carousel, Image, ResponsiveImage, SvgIcon } from ".."
import { LinkType } from "../../graphql/types"
import useIsomorphicLayoutEffect from "../../helpers/useIsomorphicLayoutEffect"

export type BannerItem = {
  title: string
  titleLogoUrl?: string
  description: string
  image: ResponsiveImageType
  location: string
  link?: LinkType
}

type LocationBannersProps = {
  items: BannerItem[]
}

const LocationBanners: FC<LocationBannersProps> = ({ items }) => {
  const cursorContainer = useRef<HTMLDivElement>(null)
  const cursorArrow = useRef<HTMLButtonElement>(null)
  const [cursorActive, setCursorActive] = useState<"prev" | "next" | null>(null)
  const [cursorX, setCursorX] = useState(-100)
  const [cursorY, setCursorY] = useState(-100)
  const [emblaRef, emblaApi] = useEmblaCarousel({ duration: 40, align: "center" }, [
    Autoplay({
      delay: 8000,
    }),
  ])
  const [scrollProgress, setScrollProgress] = useState(0)

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 100)
  }, [])

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return

    onScroll(emblaApi)
    emblaApi.on("reInit", onScroll)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, onScroll])

  useGSAP(
    () => {
      const render = () => {
        gsap.to(cursorArrow.current, {
          duration: 0.3,
          x: cursorX,
          y: cursorY,
          ease: "power2.easeOut",
        })
      }
      const requestId = requestAnimationFrame(render)
      return () => cancelAnimationFrame(requestId)
    },
    {
      scope: cursorContainer,
      dependencies: [cursorX, cursorY, cursorArrow],
    }
  )

  useIsomorphicLayoutEffect(() => {
    const updateCursor = (e: MouseEvent) => {
      setCursorX(e.clientX)
      setCursorY(e.clientY)
    }
    document.addEventListener("mousemove", updateCursor)
    return () => document.removeEventListener("mousemove", updateCursor)
  }, [cursorX, cursorY])

  return (
    <Box ref={cursorContainer} position="relative" pb={{ xs: 12, md: 24 }}>
      <ButtonBase
        ref={cursorArrow}
        sx={{
          color: "white",
          position: "fixed",
          opacity: cursorActive === null ? 0 : 1,
          top: 0,
          left: 0,
          zIndex: 100,
          pointerEvents: "none",
          cursor: "none",
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          position="relative"
          width={70}
          height={70}
          bgcolor={(theme) => theme.palette["neutral.100"].main}
          boxShadow={(theme) => `2px 2px 8px ${alpha(theme.palette["neutral.900"].main, 0.08)}`}
          borderRadius={9999}
          sx={{ transform: "translate(-50%, -50%)" }}
        >
          <SvgIcon
            variant="arrow"
            sx={{
              fill: (theme) => theme.palette["neutral.700"].main,
              ...(cursorActive === "next" && { transform: "rotate(180deg)" }),
            }}
          />
        </Stack>
      </ButtonBase>
      <Carousel.Root
        carouselRef={emblaRef}
        maxWidth="100vw"
        height={{ xs: 500, md: 560 }}
        actions={
          <>
            <Box position="absolute" bottom="27.5px" left="45px">
              <LinearProgress
                variant="determinate"
                value={scrollProgress}
                sx={{
                  "width": "90px",
                  "height": 4,
                  "display": { xs: "block", md: "none" },
                  "&.MuiLinearProgress-root": {
                    backgroundColor: (theme) => alpha(theme.palette["neutral.100"].main, 0.3),
                  },

                  "& .MuiLinearProgress-bar": {
                    backgroundColor: (theme) => theme.palette["neutral.100"].main,
                  },
                  "zIndex": 0,
                }}
              />
            </Box>
          </>
        }
      >
        {items.map((item, i) => (
          <Banner
            key={i}
            {...item}
            isActive={emblaApi?.selectedScrollSnap() === i}
            scrollPrev={scrollPrev}
            scrollNext={scrollNext}
            scrollTo={() => scrollTo(i)}
            onNextEnter={() => setCursorActive("next")}
            onPrevEnter={() => setCursorActive("prev")}
            onMouseLeave={() => setCursorActive(null)}
            canScrollNext={emblaApi?.canScrollNext() as boolean}
            canScrollPrev={emblaApi?.canScrollPrev() as boolean}
          />
        ))}
      </Carousel.Root>
    </Box>
  )
}
export default LocationBanners

type BannerProps = BannerItem & {
  isActive: boolean
  scrollPrev?: () => void
  scrollNext?: () => void
  scrollTo?: () => void
  onPrevEnter?: MouseEventHandler<HTMLButtonElement>
  onNextEnter?: MouseEventHandler<HTMLButtonElement>
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>
  canScrollNext: boolean
  canScrollPrev: boolean
}
const Banner: FC<BannerProps> = ({
  title,
  titleLogoUrl,
  description,
  image,
  link,
  location,
  isActive,
  scrollPrev,
  scrollNext,
  scrollTo,
  onPrevEnter,
  onNextEnter,
  onMouseLeave,
  canScrollNext,
  canScrollPrev,
}) => {
  return (
    <Carousel.Slide
      mx={2}
      borderRadius={1.5}
      position="relative"
      overflow="hidden"
      maxWidth={{ lg: "95%" }}
      color={(theme) => theme.palette["neutral.100"].main}
      zIndex={50}
      tabIndex={0}
      onFocus={scrollTo}
      sx={{ outline: 0 }}
    >
      <BannerImage image={image} />
      {canScrollPrev && (
        <ButtonBase
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: "50%",
            cursor: "none",
            zIndex: 1,
          }}
          onClick={isActive ? scrollPrev : scrollNext}
          onMouseEnter={isActive ? onPrevEnter : onNextEnter}
          onMouseLeave={onMouseLeave}
          tabIndex={-1}
        />
      )}
      <BannerContent>
        {titleLogoUrl && titleLogoUrl !== "#" && <BannerLogo src={titleLogoUrl} alt={title} />}
        <BannerTitle title={title} />
        <BannerDescription description={description} />
        {link && (
          <BannerCTA href={link.uri} tabIndex={isActive ? 0 : -1}>
            {link.title}
          </BannerCTA>
        )}
        <BannerLocation location={location} />
      </BannerContent>

      {canScrollNext && (
        <ButtonBase
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: "50%",
            cursor: "none",
          }}
          onClick={isActive ? scrollNext : scrollPrev}
          onMouseEnter={isActive ? onNextEnter : onPrevEnter}
          onMouseLeave={onMouseLeave}
          tabIndex={-1}
        />
      )}
    </Carousel.Slide>
  )
}

const BannerImage: FC<Pick<BannerItem, "image">> = ({ image }) => {
  return (
    <ResponsiveImage
      image={image}
      position="absolute"
      width="100%"
      height="100%"
      zIndex={-1}
      sx={{
        objectFit: "cover",
        pointerEvents: "none",
      }}
    />
  )
}

const BannerContent: FCWC = ({ children }) => {
  return (
    <Box
      bgcolor={(theme) => alpha(theme.palette["neutral.900"].main, 0.3)}
      position="absolute"
      bottom={0}
      left={0}
      right={0}
      top={0}
    >
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        justifyContent="center"
        px={{ xs: 5, md: 32.5 }}
        py={{ xs: 5, md: 28 }}
        zIndex={1}
      >
        {children}
      </Box>
    </Box>
  )
}

const BannerLogo: FC<{ src: string; alt: string }> = ({ src, alt }) => {
  return (
    <Box mb={{ md: 10 }}>
      <Image src={src} alt={alt} data-chromatic="ignore" sx={{ maxHeight: 56 }} />
    </Box>
  )
}

const BannerTitle: FC<{ title: string }> = ({ title }) => {
  return (
    <Typography
      component="h2"
      variant="heading.2xl"
      maxWidth={600}
      mb={5}
      mt={{ xs: "auto", md: 0 }}
    >
      {title}
    </Typography>
  )
}

const BannerDescription: FC<Pick<BannerItem, "description">> = ({ description }) => {
  const truncatedDescription = truncateString(description, 120)
  return (
    <Typography
      component="p"
      variant="body.md"
      maxWidth={450}
      dangerouslySetInnerHTML={{ __html: truncatedDescription }}
    />
  )
}

const BannerCTA: FCWC<LinkProps> = ({ children, ...props }) => {
  return (
    <Box mt={{ xs: 8, md: 14 }} mb={{ xs: 14, md: 0 }}>
      <Link
        sx={(theme) => ({
          ...theme.typography["link.md"],
          backgroundColor: theme.palette["neutral.100"].main,
          borderRadius: 1,
          color: theme.palette["neutral.700"].main,
          display: "inline-block",
          px: 9,
          py: { xs: 4.5, md: 6 },
          textAlign: "center",
          width: { xs: "100%", md: "auto" },
          position: "relative",
          zIndex: 10,
        })}
        underline="none"
        {...props}
      >
        {children}
      </Link>
    </Box>
  )
}

const BannerLocation: FC<{ location?: string }> = ({ location }) => {
  return (
    <>
      {location && (
        <Stack
          bgcolor={(theme) => alpha(theme.palette["neutral.900"].main, 0.05)}
          borderRadius={9999}
          direction="row"
          columnGap={1.6}
          alignItems="center"
          position="absolute"
          right={20}
          bottom={15}
          px={3}
          py={2}
          sx={{ backdropFilter: "blur(4px)" }}
        >
          <SvgIcon variant="location" size={{ xs: 8, md: 12 }} />
          <Typography variant="link.xs">{location}</Typography>
        </Stack>
      )}
    </>
  )
}
